import { useAtomValue } from 'jotai';

import { usePerformanceTier } from './usePerformanceTier';

import type { PermissionValue } from '@/constants/Permission';
import type { ProGateOutcomeValue } from '@/utils/performanceTier';

import { useGetUserPermissions } from '@/api/directory';
import { userInstanceAtom } from '@/atoms/userDetails';
import { Permission } from '@/constants/Permission';
import { ProGateOutcome, PerformanceTier, resolveProGate } from '@/utils/performanceTier';

/**
 * Whether a Performance PRO access point is shown, locked or hidden for the reader.
 *
 * `permission` is the Performance permission the feature itself needs; leave it out for an access
 * point inside a screen the reader could already open. Answers `undefined` while the permissions it
 * depends on are still loading, so a caller renders nothing rather than flashing the wrong state. A
 * failed permissions read hides the access point, as the existing permission guard does.
 */
export const useProGate = (permission?: PermissionValue): ProGateOutcomeValue | undefined => {
    const { tier } = usePerformanceTier();
    const { isAdminUser } = useAtomValue(userInstanceAtom);
    const { data: permissions, isError } = useGetUserPermissions();

    // An administrator holds every Performance permission, so the answer never waits on them.
    if (isAdminUser) {
        return resolveProGate({
            tier,
            hasFeaturePermission: true,
            isAdmin: true,
            canManageAllDepartments: true,
        });
    }

    // A PRO company only needs the feature's own permission; one that needs none is shown at once.
    if (tier === PerformanceTier.Pro && !permission) {
        return ProGateOutcome.Show;
    }

    if (isError) {
        return ProGateOutcome.Hide;
    }

    if (!permissions) {
        return undefined;
    }

    return resolveProGate({
        tier,
        hasFeaturePermission: !permission || Boolean(permissions[permission]),
        isAdmin: false,
        canManageAllDepartments: Boolean(permissions[Permission.ManageAllDepartmentsPerformance]),
    });
};
